/**
 * SplashScreen - "Alpine Success" Design
 *
 * Écran de démarrage modernisé avec le design system Warm Success
 * Cohérent avec l'onboarding V3:
 * - Fond avec gradient et texture subtile
 * - Logo animé avec effet de pulse
 * - Indicateur de chargement élégant
 * - Éléments décoratifs flottants
 * - Animations fluides avec Framer Motion
 */

import React, { useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  warmSuccessPrimary,
  warmSuccessSecondary,
  warmSuccessNeutral,
  warmSuccessBackground,
} from '../theme/warmSuccessDesignSystem';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const SplashContainer = styled(Box)(() => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  overflow: 'hidden',

  // Base warm cream background
  backgroundColor: warmSuccessBackground.default,

  // Layered gradient creating depth - same as OnboardingLayoutV3
  backgroundImage: `
    radial-gradient(ellipse 80% 50% at 50% -20%, ${alpha(warmSuccessPrimary[100], 0.5)} 0%, transparent 50%),
    radial-gradient(ellipse 60% 40% at 80% 100%, ${alpha(warmSuccessSecondary[100], 0.4)} 0%, transparent 40%),
    radial-gradient(ellipse 50% 30% at 10% 80%, ${alpha(warmSuccessPrimary[50], 0.5)} 0%, transparent 35%)
  `,
}));

// Topographic pattern overlay
const TopographicOverlay = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.025,
  pointerEvents: 'none',
  backgroundImage: `
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 50px,
      ${warmSuccessPrimary[700]} 50px,
      ${warmSuccessPrimary[700]} 51px
    ),
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 50px,
      ${warmSuccessPrimary[700]} 50px,
      ${warmSuccessPrimary[700]} 51px
    )
  `,
}));

// Grain texture for depth
const GrainOverlay = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  opacity: 0.3,
  pointerEvents: 'none',
  mixBlendMode: 'overlay',
  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
}));

// Floating decorative elements
const FloatingElement = styled('div', {
  shouldForwardProp: (prop) => prop !== '$variant',
})<{ $variant: 'primary' | 'secondary' | 'accent' }>(
  ({ $variant }) => ({
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(1px)',
    opacity: 0.5,
    ...($variant === 'primary' && {
      background: `linear-gradient(135deg, ${warmSuccessPrimary[300]}, ${warmSuccessPrimary[400]})`,
    }),
    ...($variant === 'secondary' && {
      background: `linear-gradient(135deg, ${warmSuccessSecondary[300]}, ${warmSuccessSecondary[400]})`,
    }),
    ...($variant === 'accent' && {
      background: `linear-gradient(135deg, ${alpha(warmSuccessPrimary[500], 0.3)}, ${alpha(warmSuccessSecondary[400], 0.3)})`,
    }),
  })
);

// Logo container with glassmorphism
const LogoContainer = styled('div')(() => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 40,
  borderRadius: 32,

  // Subtle glassmorphism
  background: alpha('#FFFFFF', 0.4),
  backdropFilter: 'blur(20px) saturate(150%)',
  WebkitBackdropFilter: 'blur(20px) saturate(150%)',

  // Subtle border
  border: `1px solid ${alpha(warmSuccessPrimary[200], 0.3)}`,

  // Soft shadow
  boxShadow: `
    0 4px 24px ${alpha(warmSuccessNeutral[900], 0.06)},
    0 12px 48px ${alpha(warmSuccessPrimary[900], 0.08)},
    inset 0 1px 0 ${alpha('#FFFFFF', 0.6)}
  `,
}));

// Logo mark
const LogoMark = styled('div')(() => ({
  width: 80,
  height: 80,
  borderRadius: 20,
  background: `linear-gradient(135deg, ${warmSuccessPrimary[600]} 0%, ${warmSuccessPrimary[700]} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: `
    0 8px 32px ${alpha(warmSuccessPrimary[600], 0.4)},
    inset 0 1px 0 ${alpha('#FFFFFF', 0.2)}
  `,
  marginBottom: 20,
}));

// Loading bar container
const LoadingBarContainer = styled(Box)(() => ({
  width: 200,
  height: 4,
  borderRadius: 2,
  backgroundColor: alpha(warmSuccessPrimary[200], 0.5),
  overflow: 'hidden',
  marginTop: 24,
}));

// Loading bar progress
const LoadingBarProgress = styled('div')(() => ({
  height: '100%',
  borderRadius: 2,
  background: `linear-gradient(90deg, ${warmSuccessPrimary[500]}, ${warmSuccessPrimary[400]}, ${warmSuccessPrimary[500]})`,
  backgroundSize: '200% 100%',
}));

// Pulse ring around logo
const PulseRing = styled('div')(() => ({
  position: 'absolute',
  width: 120,
  height: 120,
  borderRadius: 28,
  border: `2px solid ${alpha(warmSuccessPrimary[400], 0.3)}`,
  pointerEvents: 'none',
}));

// ============================================================================
// COMPONENT
// ============================================================================

const SplashScreen: React.FC = () => {
  // Generate floating elements positions
  const floatingElements = useMemo(() => [
    { id: 1, variant: 'primary' as const, size: 140, x: '8%', y: '12%', delay: 0 },
    { id: 2, variant: 'secondary' as const, size: 100, x: '88%', y: '18%', delay: 0.5 },
    { id: 3, variant: 'accent' as const, size: 80, x: '5%', y: '78%', delay: 1 },
    { id: 4, variant: 'primary' as const, size: 60, x: '92%', y: '72%', delay: 1.5 },
    { id: 5, variant: 'secondary' as const, size: 120, x: '78%', y: '88%', delay: 0.8 },
    { id: 6, variant: 'accent' as const, size: 50, x: '15%', y: '45%', delay: 1.2 },
  ], []);

  return (
    <SplashContainer>
      {/* CSS keyframes replacing the former Framer Motion animations */}
      <style>{`
        @keyframes splashFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(15px, -25px) scale(1.08); }
        }
        @keyframes splashLogoIn {
          from { opacity: 0; transform: translateY(30px) scale(0.8); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes splashPulseRing {
          0% { transform: scale(1); opacity: 0.5; }
          50%, 100% { transform: scale(1.3); opacity: 0; }
        }
        @keyframes splashLogoGlow {
          0%, 100% { box-shadow: 0 8px 32px ${alpha(warmSuccessPrimary[600], 0.4)}; }
          50% { box-shadow: 0 12px 48px ${alpha(warmSuccessPrimary[600], 0.5)}; }
        }
        @keyframes splashFadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes splashFadeScale {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes splashFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes splashLoadingBarX {
          from { transform: translateX(-100%); }
          to { transform: translateX(100%); }
        }
        @keyframes splashLoadingBarBg {
          from { background-position: 0% 50%; }
          to { background-position: 100% 50%; }
        }
      `}</style>

      {/* Background layers */}
      <TopographicOverlay />
      <GrainOverlay />

      {/* Floating decorative elements */}
      {floatingElements.map((el) => (
        <FloatingElement
          key={el.id}
          $variant={el.variant}
          style={{
            width: el.size,
            height: el.size,
            left: el.x,
            top: el.y,
            animation: 'splashFloat 7s ease-in-out infinite',
            animationDelay: `${el.delay}s`,
          }}
        />
      ))}

      {/* Main content */}
      <LogoContainer
        style={{
          animation: 'splashLogoIn 0.7s cubic-bezier(0.4, 0, 0.2, 1) both',
        }}
      >
        {/* Pulse rings */}
        <PulseRing
          style={{ animation: 'splashPulseRing 2s ease-out infinite' }}
        />
        <PulseRing
          style={{ animation: 'splashPulseRing 2s ease-out 0.6s infinite' }}
        />

        {/* Logo mark */}
        <LogoMark
          style={{ animation: 'splashLogoGlow 2s ease-in-out infinite' }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 800,
              fontSize: '2.5rem',
              fontFamily: '"DM Serif Display", Georgia, serif',
              lineHeight: 1,
            }}
          >
            F
          </Typography>
        </LogoMark>

        {/* Brand name */}
        <div
          style={{ animation: 'splashFadeUp 0.5s ease 0.3s both' }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              color: warmSuccessNeutral[800],
              letterSpacing: '-0.02em',
              textAlign: 'center',
            }}
          >
            Foot in the Door
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: warmSuccessNeutral[500],
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontWeight: 500,
              textAlign: 'center',
              mt: 0.5,
            }}
          >
            Suisse
          </Typography>
        </div>

        {/* Loading bar */}
        <div
          style={{ animation: 'splashFadeScale 0.4s ease 0.5s both' }}
        >
          <LoadingBarContainer>
            <LoadingBarProgress
              style={{
                width: '50%',
                animation:
                  'splashLoadingBarX 1.5s ease-in-out infinite, splashLoadingBarBg 2s linear infinite',
              }}
            />
          </LoadingBarContainer>
        </div>
      </LogoContainer>

      {/* Trust badges */}
      <div
        style={{ animation: 'splashFadeIn 0.5s ease 0.8s both' }}
      >
        <Stack
          direction="row"
          spacing={3}
          justifyContent="center"
          sx={{ mt: 5 }}
        >
          {[
            { icon: '🔒', text: 'Données sécurisées' },
            { icon: '🇨🇭', text: 'Made in Switzerland' },
            { icon: '⚡', text: 'IA de pointe' },
          ].map((badge, index) => (
            <div
              key={index}
              style={{ animation: `splashFadeUp 0.4s ease ${1 + index * 0.1}s both` }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{
                  color: warmSuccessNeutral[500],
                  fontSize: '0.75rem',
                  fontWeight: 500,
                }}
              >
                <span>{badge.icon}</span>
                <span>{badge.text}</span>
              </Stack>
            </div>
          ))}
        </Stack>
      </div>

      {/* Decorative bottom wave */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 120,
          background: `linear-gradient(to top, ${alpha(warmSuccessPrimary[50], 0.5)}, transparent)`,
          pointerEvents: 'none',
        }}
      />
    </SplashContainer>
  );
};

export default SplashScreen;
